import { useDisclosure } from '@chakra-ui/react';
import {
  DiscountApplicationMethodEnum,
  IQuoteRequestSchema,
} from '@monetize/types/app';
import { DEFAULT_PAGER, Maybe } from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { toDateShort } from '@monetize/utils/core';
import { parseISO } from 'date-fns/parseISO';
import { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  useUpdatePoNumberOnQuote,
  useUpdateVatNumberOnQuote,
} from '../../../../api/accountsService';
import { useGetBlueprintQuotes } from '../../../../api/cpqService';
import {
  MBox,
  MButton,
  MFlex,
  QuoteSelectInput,
} from '../../../../components/Monetize';
import { DatePicker } from '../../../../components/Monetize/DatePicker/DatePicker';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { ChangeOwnerV2 } from '../../../../components/Quotes/ChangeOwnerV2';
import { QuoteBasicInfoLine } from '../../../../components/Quotes/QuoteBasicInfoLine';
import QuoteBasicOpportunity from '../../../../components/Quotes/QuoteBasicOpportunity';
import QuoteExpirationDateSelector from '../../../../components/Quotes/QuoteExpirationDateSelector';
import { QuoteInlineEditableTextField } from '../../../../components/Quotes/QuoteInlineEditableTextField';
import { QuoteOutputLanguageSelection } from '../../../../components/Quotes/QuoteOutputLanguageSelection';
import { DISCOUNT_APPLICATION_METHOD_OPTIONS } from '../../../../constants/discounts';
import { QUOTE_EXPIRATION_DATE_EDITABLE_STATUSES } from '../../../../constants/quotes';
import {
  useExperienceConfiguration,
  useQuoteSettings,
} from '../../../../hooks';
import { useFlags } from '../../../../services/launchDarkly';
import { getQuoteType } from '../../../../utils';
import { getExpirationColors } from '../../../../utils/quotes';
import { useQuoteContext } from '../../Quote/quoteContext';
import { ChangeQuoteBlueprintModal } from './ChangeQuoteBlueprintModal';

export const QuoteBasicDetails = () => {
  const {
    handleSubmitButtonWithoutDirtyCheck,
    isReadOnly,
    isReadOnlyQuote,
    quoteData: { quote, setQuote, fetchQuote },
    useAmendmentV2,
    opportunityLinkData,
    handleChangeOwner,
    setIsQuoteSaving,
  } = useQuoteContext();
  const { isAmendment, isNew } = getQuoteType(quote);
  const { control } = useFormContext<IQuoteRequestSchema>();
  const { quoteSettings } = useQuoteSettings();
  const tenantSettings = useExperienceConfiguration(
    ExperienceConfigurationKeys.tenantSettings,
  );
  const { enableDiscountApplicationMethodSettings } = useFlags();
  const blueprintModalStatus = useDisclosure();
  const { mutateAsync: doUpdatePoNumber } = useUpdatePoNumberOnQuote();
  const { mutateAsync: doUpdateVatNumber } = useUpdateVatNumberOnQuote();

  const handleSavePoNumber = useCallback(
    async (poNumber: Maybe<string>) => {
      if (!quote) {
        return;
      }
      await doUpdatePoNumber({ quoteId: quote.id, poNumber });
      setQuote({ ...quote, purchaseOrderNumber: poNumber });
      await fetchQuote(quote.id);
    },
    [doUpdatePoNumber, fetchQuote, quote, setQuote],
  );

  const handleSaveVatNumber = useCallback(
    async (vatNumber: Maybe<string>) => {
      if (!quote) {
        return;
      }
      await doUpdateVatNumber({ quoteId: quote.id, vatNumber });
      setQuote({ ...quote, vatNumber });
      await fetchQuote(quote.id);
    },
    [doUpdateVatNumber, fetchQuote, quote, setQuote],
  );

  const { data: blueprintsData } = useGetBlueprintQuotes(
    {
      config: { ...DEFAULT_PAGER, rows: 1 },
      filters: quote?.currency
        ? { quoteCurrency: quote.currency, isActive: 'true' }
        : undefined,
    },
    {
      enabled: !!quote?.currency && !isReadOnly,
    },
  );

  const hasBothDiscountTypes = useMemo(
    () =>
      quote?.quoteOfferings.some(({ items }) =>
        items.some(
          ({ discounts, customDiscountAmountOrPercent }) =>
            discounts.length > 0 && !!customDiscountAmountOrPercent,
        ),
      ) ?? false,
    [quote?.quoteOfferings],
  );

  const hasActiveBlueprints = (blueprintsData?.totalElements ?? 0) > 0;
  const hasBlueprint =
    !!quote?.metadata?.bluePrintId || !!quote?.metadata?.sourceBluePrintQuoteId;

  const showBlueprintOption = isNew && hasActiveBlueprints && !isReadOnly;

  if (!quote) {
    return null;
  }

  return (
    <MBox>
      <QuoteBasicInfoLine
        title="Owner"
        inputId="quote-header-owner"
        fieldKey="owner"
      >
        <MBox mr={isReadOnly ? 0 : '-3'}>
          <ChangeOwnerV2
            inputId="quote-header-owner"
            isReadOnly={isReadOnly}
            value={quote?.owner}
            ownerName={quote?.ownerName}
            onChange={(e) => handleChangeOwner(String(e))}
          />
        </MBox>
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine title="Opportunity">
        {opportunityLinkData && (
          <QuoteBasicOpportunity {...opportunityLinkData} />
        )}
      </QuoteBasicInfoLine>

      {isAmendment && !useAmendmentV2 && (
        <QuoteBasicInfoLine
          title="Amendment Date"
          fieldKey="contractAmendmentDate"
        >
          <MBox mr="-3">
            <Controller
              name="contractAmendmentDate"
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <DatePicker
                  {...rest}
                  triggerType="button"
                  btnText={toDateShort(quote?.contractAmendmentDate || null)}
                  minDate={
                    quote?.contractStartDate
                      ? parseISO(quote.contractStartDate)
                      : undefined
                  }
                  maxDate={
                    quote?.contractEndDate
                      ? parseISO(quote.contractEndDate)
                      : undefined
                  }
                  onChange={(date) => {
                    onChange(date);
                    handleSubmitButtonWithoutDirtyCheck();
                  }}
                  isDisabled={!quote}
                  isReadOnly={isReadOnly}
                  btnProps={{
                    my: 0,
                    'data-testid': 'contractStartDateBtn',
                  }}
                  readonlyContentProps={{ pb: 0, pt: 1, mr: 2 }}
                />
              )}
            />
          </MBox>
        </QuoteBasicInfoLine>
      )}

      <QuoteBasicInfoLine title="Expiration Date" fieldKey="expirationDate">
        {!QUOTE_EXPIRATION_DATE_EDITABLE_STATUSES.includes(quote.status) ? (
          <MFlex align="center" mr="-3">
            <Controller
              name="expirationDate"
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <DatePicker
                  data-testid="date-picker-expirationDate"
                  triggerType="button"
                  btnText={toDateShort(quote?.expirationDate || null)}
                  {...rest}
                  isDisabled={isReadOnly}
                  isReadOnly={isReadOnly}
                  minDate={new Date()}
                  placeholder="Select End Date"
                  onChange={(date) => {
                    onChange(date);
                    handleSubmitButtonWithoutDirtyCheck();
                  }}
                  readonlyContentProps={{ pb: 0, pt: 1.5, mr: 2 }}
                  btnProps={{
                    my: 0,
                    color: getExpirationColors(quote?.expirationDate),
                  }}
                />
              )}
            />
          </MFlex>
        ) : (
          <QuoteExpirationDateSelector
            expirationDate={quote?.expirationDate}
            quoteId={quote?.id}
            setQuote={setQuote}
          />
        )}
      </QuoteBasicInfoLine>

      {showBlueprintOption && (
        <QuoteBasicInfoLine
          title={hasBlueprint ? 'Created from Blueprint' : 'Use Blueprint'}
        >
          <MBox mr="-3">
            <MButton
              variant="tertiary"
              size="sm"
              onClick={blueprintModalStatus.onOpen}
              my={0}
            >
              {hasBlueprint ? 'View / Change' : 'Select'}
            </MButton>
          </MBox>
        </QuoteBasicInfoLine>
      )}

      {quoteSettings?.allowPONumberOnQuotes && (
        <QuoteBasicInfoLine title="PO Number" fieldKey="purchaseOrderNumber">
          <MBox mr="-3">
            <QuoteInlineEditableTextField
              quote={quote}
              value={quote.purchaseOrderNumber}
              placeholder="PO Number"
              maxLength={45}
              isReadOnly={isReadOnlyQuote}
              setIsQuoteSaving={setIsQuoteSaving}
              onSave={handleSavePoNumber}
              buttonProps={{ ml: '-3' }}
              previewPropsNonEdit={{
                justifyContent: 'flex-start',
              }}
            />
          </MBox>
        </QuoteBasicInfoLine>
      )}

      {quoteSettings?.allowVATNumberOnQuotes && (
        <QuoteBasicInfoLine title="VAT Number" fieldKey="vatNumber">
          <MBox mr="-3">
            <QuoteInlineEditableTextField
              quote={quote}
              value={quote.vatNumber}
              placeholder="VAT Number"
              isReadOnly={isReadOnlyQuote}
              setIsQuoteSaving={setIsQuoteSaving}
              onSave={handleSaveVatNumber}
              buttonProps={{ ml: '-3' }}
              previewPropsNonEdit={{
                justifyContent: 'flex-start',
              }}
            />
          </MBox>
        </QuoteBasicInfoLine>
      )}

      {tenantSettings.enableQuoteOutputLanguageSelection && (
        <QuoteBasicInfoLine title="Output Language" fieldKey="outputLanguage">
          <MBox mr="-3">
            <QuoteOutputLanguageSelection
              quote={quote}
              setQuote={setQuote}
              fetchQuote={fetchQuote}
              setIsQuoteSaving={setIsQuoteSaving}
              buttonProps={{ ml: '-3' }}
            />
          </MBox>
        </QuoteBasicInfoLine>
      )}

      {hasBothDiscountTypes && enableDiscountApplicationMethodSettings && (
        <QuoteBasicInfoLine
          title="Discount Application Method"
          fieldKey="discountApplicationMethod"
        >
          <MBox mr={isReadOnly || isAmendment ? 0 : '-3'}>
            <Controller
              name="discountApplicationMethod"
              control={control}
              render={({ field: { onChange, name, ...rest } }) => (
                <MCustomSelect
                  name={name}
                  isDisabled={isReadOnly || isAmendment}
                  isReadOnly={isReadOnly || isAmendment}
                  MainInputComponent={QuoteSelectInput}
                  items={DISCOUNT_APPLICATION_METHOD_OPTIONS}
                  popOverProps={{
                    positioning: { placement: 'bottom-end', sameWidth: false },
                  }}
                  isLazy
                  popOverContentProps={{ minWidth: '10rem' }}
                  onChange={(val: DiscountApplicationMethodEnum) => {
                    onChange(val);
                    handleSubmitButtonWithoutDirtyCheck();
                  }}
                  showContentInInput
                  {...rest}
                />
              )}
            />
          </MBox>
        </QuoteBasicInfoLine>
      )}

      <ChangeQuoteBlueprintModal
        quote={quote}
        isOpen={blueprintModalStatus.open}
        onClose={blueprintModalStatus.onClose}
        onSuccess={() => fetchQuote(quote.id)}
      />
    </MBox>
  );
};
